import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isEmpty, isNil } from 'lodash';

import { ConfirmModal } from '~/components/ConfirmModal';
import { LoadingSwitch, PropertyDetails, Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import StatusChipCore from '~/components/core/StatusChipCore';
import { PencilIcon } from '~/components/icons';
import Switch from '~/components/ui/atoms/Switch';
import { SorConfigurationResponseSorAuthMode } from '~/server_shared/openapi/data-contracts';

import { useSorConfiguration } from './hooks/useSorConfiguration';
import SorSetupDialog from './SorSetupDialog';
import TEXTS from './texts';
import type { SorConfigurationParams, SorConnectionTestResult } from './types';

// Columns sized to their values rather than a quarter of the page each, which left wide gaps on a wide screen.
const detailsCn = 'grid grid-cols-[repeat(auto-fill,minmax(180px,260px))] gap-x-40 gap-y-16';

const ConnectionStatus = ({ connectionTest }: { connectionTest: SorConnectionTestResult | null }) => {
  if (!connectionTest) {
    return <StatusChipCore status={TEXTS.notTested} color="grey" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-8">
        <StatusChipCore
          status={connectionTest.is_reachable ? TEXTS.connected : TEXTS.connectionFailed}
          color={connectionTest.is_reachable ? 'green' : 'red'}
        />
        {!isNil(connectionTest.latency_ms) && (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {TEXTS.latency(connectionTest.latency_ms)}
          </Text>
        )}
      </div>
      {!isNil(connectionTest.error_code) && (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          {TEXTS.connectionError(connectionTest.error_code, connectionTest.error_message ?? '')}
        </Text>
      )}
    </div>
  );
};

interface SorConfigurationTabProps {
  /** Unset when the user may not open the Issues tab. */
  issuesUrl?: string;
  /** Five Sigma's internal admin, the only place the configuration is saved; elsewhere it is read-only. */
  isInternal?: boolean;
}

const isUserTokenAuthMode = (authMode?: string | null): boolean =>
  authMode === SorConfigurationResponseSorAuthMode.UserSsoToken;

const SorConfigurationTab: React.FC<SorConfigurationTabProps> = ({ issuesUrl, isInternal = false }) => {
  // Fixed when the dialog opens: a first save creates the configuration, and a retry after a failed
  // connection test must still enable it.
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [isDisableWarningOpen, setIsDisableWarningOpen] = useState(false);
  const {
    isLoading,
    isError,
    configuration,
    isSsoOfflineAccessEnabled,
    operations,
    openAttemptCounts,
    connectionTest,
    isSaving,
    isTesting,
    saveConfiguration,
    testConnection,
    reloadOpenAttemptCounts,
  } = useSorConfiguration();
  const isBusy = isSaving || isTesting;
  const isUserTokenMode = isUserTokenAuthMode(configuration?.auth_mode);
  // The user-token mode probes with the testing user's own SSO token, which a Five Sigma user does not have: an
  // organization user tests it from their own sysconfig.
  const canTestHere = !(isInternal && isUserTokenMode);

  // The test probes the saved configuration, so the settings must be saved before it runs.
  const enableIfReachable = async (authMode: string | null | undefined) => {
    if (isInternal && isUserTokenAuthMode(authMode)) {
      await saveConfiguration({ is_enabled: true });
      return;
    }
    const result = await testConnection();
    if (result.is_reachable) {
      await saveConfiguration({ is_enabled: true });
    }
  };

  const disableOrWarn = async () => {
    const counts = await reloadOpenAttemptCounts();
    if (!counts) {
      return;
    }
    if (counts.open_attempts > 0) {
      setIsDisableWarningOpen(true);
    } else {
      await saveConfiguration({ is_enabled: false });
    }
  };

  // Every request below reports its own failure, so these handlers only stop at it.
  const handleEnabledChange = async (shouldEnable: boolean) => {
    try {
      await (shouldEnable ? enableIfReachable(configuration?.auth_mode) : disableOrWarn());
    } catch {
      // Reported by the hook.
    }
  };

  const handleTestConnection = async () => {
    try {
      await testConnection();
    } catch {
      // Reported by the hook.
    }
  };

  const handleConfirmDisable = async () => {
    try {
      await saveConfiguration({ is_enabled: false });
    } catch {
      // Reported by the hook.
    } finally {
      setIsDisableWarningOpen(false);
    }
  };

  const handleSubmit = async (params: SorConfigurationParams) => {
    try {
      await saveConfiguration(params);
    } catch {
      // Reported by the hook; the dialog stays open to save again.
      return;
    }
    const authMode = params.auth_mode ?? configuration?.auth_mode;
    try {
      if (dialogMode === 'create') {
        await enableIfReachable(authMode);
      } else if (!(isInternal && isUserTokenAuthMode(authMode))) {
        await testConnection();
      }
    } catch {
      // Reported by the hook.
    }
    // The settings are saved, so the dialog closes even when the test after them failed.
    setDialogMode(null);
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      {configuration ? (
        <div className="flex flex-col gap-20">
          <MainCard
            type="elevated"
            title={TEXTS.systemOfRecord}
            action={
              <div className="flex items-center gap-12">
                {isInternal && (
                  <IconButton
                    aria-label={TEXTS.editConfiguration}
                    onClick={() => setDialogMode('edit')}
                    disabled={isBusy}
                  >
                    <PencilIcon />
                  </IconButton>
                )}
                <Switch
                  checked={configuration.is_enabled}
                  onChange={(_event, checked) => handleEnabledChange(checked)}
                  // Read-only outside internal admin. Enabling runs the connection test, which needs a URL to call.
                  disabled={!isInternal || isBusy || (!configuration.is_enabled && !configuration.api_base_url)}
                  label={TEXTS.enabled}
                />
              </div>
            }
          >
            <div className={detailsCn}>
              <PropertyDetails title={TEXTS.displayName} text={configuration.display_name} />
              <PropertyDetails title={TEXTS.apiBaseUrl} text={configuration.api_base_url} />
              <PropertyDetails title={TEXTS.authentication} text={TEXTS.authModes[configuration.auth_mode]} />
              {isUserTokenMode ? (
                <PropertyDetails title={TEXTS.ssoTokenScope} text={configuration.sso_token_scope} />
              ) : (
                <PropertyDetails title={TEXTS.apiToken} text={configuration.api_token_output} />
              )}
              {configuration.extra_header_enabled && (
                <PropertyDetails
                  title={TEXTS.extraHeader}
                  text={`${configuration.extra_header_name}: ${configuration.extra_header_value_output}`}
                />
              )}
              {/* Two columns: one cuts its value short. */}
              <div className="col-span-2">
                <PropertyDetails
                  title={TEXTS.timeouts}
                  text={TEXTS.timeoutsValue(
                    configuration.connect_timeout_seconds,
                    configuration.read_timeout_seconds,
                    configuration.max_tries
                  )}
                />
              </div>
            </div>
            {isUserTokenMode && !isSsoOfflineAccessEnabled && (
              <AlertBanner alertType={AlertBanner.ALERT_TYPES.WARNING} note={TEXTS.offlineAccessWarning} withIcon />
            )}
            {canTestHere ? (
              <div className="flex items-end justify-between gap-24">
                <PropertyDetails
                  title={TEXTS.status}
                  text={null}
                  textComponent={<ConnectionStatus connectionTest={connectionTest} />}
                />
                {/* PropertyDetails takes the row's full width, so the button must not shrink: shrunk, its label
                    wrapped onto two lines and overflowed its outline. */}
                <Button
                  variant="outlined"
                  color="primary"
                  className="shrink-0 whitespace-nowrap"
                  onClick={handleTestConnection}
                  disabled={isBusy || !configuration.api_base_url}
                >
                  {TEXTS.testConnection}
                </Button>
              </div>
            ) : (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                {TEXTS.userTokenTestedByCustomer}
              </Text>
            )}
            {openAttemptCounts && (
              <div className="flex items-center gap-8">
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  {TEXTS.openAttemptCounts(openAttemptCounts.open_attempts, openAttemptCounts.needs_attention)}
                </Text>
                {issuesUrl && <Link to={issuesUrl}>{TEXTS.issuesLink}</Link>}
              </div>
            )}
          </MainCard>
          <MainCard type="elevated" title={TEXTS.operationsCommittedAtSor}>
            {isEmpty(operations) ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                {TEXTS.noOperations}
              </Text>
            ) : (
              <div className={detailsCn}>
                {operations.map(({ operation, label }) => (
                  <PropertyDetails
                    key={operation}
                    title={label}
                    text={configuration.enabled_operations.includes(operation) ? TEXTS.enabled : TEXTS.disabled}
                  />
                ))}
              </div>
            )}
          </MainCard>
        </div>
      ) : isInternal ? (
        <Button
          variant="contained"
          color="primary"
          className="my-40"
          onClick={() => setDialogMode('create')}
          disabled={isBusy}
        >
          {TEXTS.enableSystemOfRecord}
        </Button>
      ) : (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="my-40">
          {TEXTS.notConfigured}
        </Text>
      )}
      {dialogMode && (
        <SorSetupDialog
          configuration={dialogMode === 'edit' ? configuration : null}
          operations={operations}
          isSsoOfflineAccessEnabled={isSsoOfflineAccessEnabled}
          onSubmit={handleSubmit}
          onClose={() => setDialogMode(null)}
        />
      )}
      <ConfirmModal
        isOpen={isDisableWarningOpen}
        isSubmitting={isSaving}
        title={TEXTS.disableWarning.title}
        contentText={
          openAttemptCounts &&
          TEXTS.disableWarning.content(openAttemptCounts.open_attempts, openAttemptCounts.needs_attention)
        }
        primaryButtonName={TEXTS.disableWarning.confirm}
        onPrimaryBtnClick={handleConfirmDisable}
        onClose={() => setIsDisableWarningOpen(false)}
      />
    </LoadingSwitch>
  );
};

export default SorConfigurationTab;
